import { useCallback, useEffect, useMemo, useState, type PointerEvent as ReactPointerEvent } from 'react'
import { useAtom, useAtomValue } from 'jotai'

import { useSidepanelState } from '@renderer/overlay/atoms'
import { useForYouChatsFeed } from '@renderer/overlay/hooks/use-running-chats'
import type { SidepanelChat } from '@renderer/overlay/hooks/running-chat-selection'
import { UnsavedChangesDialog } from '@renderer/overlay/Sidepanel/views/action-view/nested/unsaved-changes-dialog'
import { actionInlineDraftsAtom } from '@renderer/overlay/Sidepanel/views/action-view/inline-draft-state'
import { requiresExplicitSave } from '@renderer/overlay/Sidepanel/views/action-view/inline-drafts'
import {
  isEditableActionStatus,
  useStagedActionDerivations,
} from '@renderer/overlay/Sidepanel/hooks/use-staged-actions'
import { useUnsavedChangesGuard } from '@renderer/overlay/Sidepanel/views/action-view/use-unsaved-changes-guard'
import { useReplaceView, useUpdateTopViewContext, useViewActions } from '../../view-atoms'
import { useViewScope } from '../../view-state-atoms'
import { useViewContext, useViewEntranceSettled } from '../../view-context'
import { SkillCompareBar } from '../../components/skill-compare-bar'
import { View } from '../../components/view'
import { useRenderedChatPreview } from '../chat-view/use-visible-chat-preview'
import { ConversationChatDrawer } from '../../components/chat/conversation-chat-drawer'
import { latestActionChatCaptions, shouldAnimateActionControlsHeight } from './chat-output'
import { CompletedOutput } from './nested/completed-output'
import { savedDraftFocusResetAtom } from './saved-draft-state'
import { chatOutputContextOf, useChatOutputViewModel, type ChatOutputViewContext } from './use-chat-output-view-model'
import { useChatOutputGlowTint } from './use-chat-output-glow-tint'

export type { ChatOutputViewContext }

interface ActionFocusState {
  actionId?: string
  animateHeight: boolean
}

/**
 * Prototype view: the latest completed turn of a chat, including both its
 * assistant text and artifacts, without the full conversation.
 */
export function ChatOutputView() {
  const { context } = useViewContext<ChatOutputViewContext>()
  const model = useChatOutputViewModel(context)
  const glowTint = useChatOutputGlowTint(model.stagedActions, model.stagedActionIds)
  const entranceSettled = useViewEntranceSettled()
  const sidepanelState = useSidepanelState()
  const updateOutputContext = useUpdateTopViewContext('chatOutput')
  const replaceView = useReplaceView()
  const { openView } = useViewActions()
  const { data: forYouChats } = useForYouChatsFeed()
  const [{ actionId: focusedActionId, animateHeight }, setActionFocus] = useState<ActionFocusState>({
    actionId: context?.focusedActionId,
    animateHeight: false,
  })
  const drafts = useAtomValue(actionInlineDraftsAtom)
  const isLeaveDialogOpen = useAtomValue(useViewScope().pendingLeave) !== null
  const { getActionStatus } = useStagedActionDerivations()
  // A stale draft on an action that already ran must not hold the view, as in the Action View.
  const editableActions = model.stagedActions.filter((action) => isEditableActionStatus(getActionStatus(action)))
  const dirtyActions = editableActions.filter((action) => requiresExplicitSave(drafts[action.id]))
  const dialogAction = dirtyActions[0] ?? model.stagedActions[0]
  const lastActionId = model.stagedActions.at(-1)?.id
  const captionByActionId = useMemo(
    () =>
      context?.conversationId
        ? latestActionChatCaptions(model.stagedActions, context.conversationId)
        : new Map<string, string>(),
    [context?.conversationId, model.stagedActions],
  )
  const lastActionHasCaption = lastActionId !== undefined && captionByActionId.has(lastActionId)
  const acknowledgementChat = useMemo<SidepanelChat | undefined>(() => {
    if (model.chat) {
      return model.chat
    }
    if (!context?.conversationId || context.previewRevision === undefined) {
      return undefined
    }
    return {
      id: context.conversationId,
      title: context.title,
      conversationType: context.conversationType,
      skillId: context.skillId,
      preview: context.preview,
      stagedActionIds: context.stagedActionIds ?? [],
      reminderIds: context.reminderIds ?? [],
      isStreaming: model.isStreaming,
      previewRevision: context.previewRevision,
      isUnread: true,
      updatedAt: '',
    }
  }, [context, model.chat, model.isStreaming])
  useRenderedChatPreview({
    conversationId: context?.conversationId,
    entranceSettled,
    hasMessages: Boolean(model.assistantText) || model.stagedActions.length > 0,
    isVisible: sidepanelState === 'open',
    acknowledgementChat,
  })
  useUnsavedChangesGuard(editableActions.map((action) => drafts[action.id]))

  const setFocusedActionId = useCallback(
    (nextActionId: string | undefined) => {
      updateOutputContext((current) =>
        current?.focusedActionId === nextActionId
          ? current
          : current
            ? { ...current, focusedActionId: nextActionId }
            : current,
      )
      setActionFocus((current) => {
        if (current.actionId === nextActionId) {
          return current
        }

        return {
          actionId: nextActionId,
          animateHeight: shouldAnimateActionControlsHeight({
            previousActionId: current.actionId,
            nextActionId,
            lastActionId,
            lastActionHasCaption,
          }),
        }
      })
    },
    [lastActionHasCaption, lastActionId, updateOutputContext],
  )

  // Track the row's draft ids, not the subset the staged-action cache has
  // resolved: that subset is empty for the window after a new turn adds drafts,
  // and holding the previous ids through it would attach an earlier turn's
  // drafts to the next follow-up.
  //
  // An empty list stays unwritten either way — it means the row hasn't loaded,
  // or For You already dropped the conversation, never that the drafts are
  // gone. Writing it would wipe ids that arrived with `openView` or a restored
  // history entry, leaving the composer with no edit targets at all. The
  // exception is a list emptied by dropping actions the chat runs itself: the
  // floating bar reads these ids, and would otherwise offer to start them.
  useEffect(() => {
    const stagedActionIds = model.stagedActionIds
    if (stagedActionIds.length === 0 && !model.hidesActionsRunInChat) {
      return
    }
    const current = context?.stagedActionIds ?? []
    if (current.length === stagedActionIds.length && current.every((id, index) => id === stagedActionIds[index])) {
      return
    }
    updateOutputContext((prev) => (prev ? { ...prev, stagedActionIds } : prev))
  }, [context?.stagedActionIds, model.hidesActionsRunInChat, model.stagedActionIds, updateOutputContext])

  const dismissChat = useCallback(
    () => updateOutputContext((current) => (current?.chat ? { ...current, chat: false } : current)),
    [updateOutputContext],
  )
  const openChat = useCallback(
    () => updateOutputContext((current) => (current && !current.chat ? { ...current, chat: true } : current)),
    [updateOutputContext],
  )
  const settleFreshSubmit = useCallback(
    () => updateOutputContext((current) => (current?.isFreshSubmit ? { ...current, isFreshSubmit: false } : current)),
    [updateOutputContext],
  )
  const handleChatActionClick = useCallback(
    (actionId: string) => {
      if (model.stagedActions.some((action) => action.id === actionId)) {
        setFocusedActionId(actionId)
        dismissChat()
        return
      }
      openView('action', { actionId }, { sourceLabel: 'Output' })
    },
    [dismissChat, model.stagedActions, openView, setFocusedActionId],
  )

  // While the focused action holds an unsaved edit, the floating bar is its
  // Save surface — and it only shows for the *focused* dirty action. Unfocusing
  // would strand the draft with no save controls, so focus holds until the
  // draft is saved or discarded.
  const isFocusedActionDirty = focusedActionId !== undefined && requiresExplicitSave(drafts[focusedActionId])

  // A persisted save (announced by the floating bar) returns the row to its
  // resting state instead of leaving the saved action focused. Consumed here so
  // a stale signal can't clear a later focus.
  const [savedDraftFocusReset, setSavedDraftFocusReset] = useAtom(savedDraftFocusResetAtom)
  useEffect(() => {
    if (!savedDraftFocusReset) return
    setSavedDraftFocusReset(null)
    if (savedDraftFocusReset.actionId === focusedActionId) {
      setFocusedActionId(undefined)
    }
  }, [focusedActionId, savedDraftFocusReset, setFocusedActionId, setSavedDraftFocusReset])

  // DOM containment keeps portaled popover events from clearing action focus.
  const clearFocusOnBackgroundPress = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (isFocusedActionDirty) return
    if (
      event.target instanceof Element &&
      event.currentTarget.contains(event.target) &&
      !event.target.closest('[data-staged-action-edit-box], button, [role="button"]')
    ) {
      setFocusedActionId(undefined)
    }
  }

  // Popovers and editors may consume Escape before this window listener.
  useEffect(() => {
    if (!focusedActionId || isFocusedActionDirty) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return
      setFocusedActionId(undefined)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [focusedActionId, isFocusedActionDirty, setFocusedActionId])

  return (
    <View bottomFadeTint={glowTint} className="p-2">
      <SkillCompareBar
        chatId={context?.conversationId}
        // A new entry, as the row would open: the other side starts with its own ids and no leftover focus.
        onSwitchSide={(conversationId) => {
          const row = forYouChats.find((chat) => chat.id === conversationId)
          replaceView('chatOutput', { id: 'chatOutput', context: row ? chatOutputContextOf(row) : { conversationId } })
        }}
      />
      <View.Body bottomInset="list" onPointerDown={clearFocusOnBackgroundPress}>
        {model.isLoadingActions ? (
          <View.Skeleton className="p-2" />
        ) : (
          <CompletedOutput
            model={model}
            captionByActionId={captionByActionId}
            focusedActionId={focusedActionId}
            animateControlsHeight={animateHeight}
            actionHotkeysEnabled={!isLeaveDialogOpen}
            onFocusAction={setFocusedActionId}
            onOpenThread={openChat}
            threadOpen={Boolean(context?.chat)}
          />
        )}
      </View.Body>
      {dialogAction && (
        <UnsavedChangesDialog
          action={dialogAction}
          draft={drafts[dialogAction.id]}
          hasUnsavedChanges={dirtyActions.length > 0}
        />
      )}
      {context?.conversationId && (
        <ConversationChatDrawer
          chatId={context.conversationId}
          open={Boolean(context.chat)}
          entranceSettled={entranceSettled}
          isFreshSubmit={context.isFreshSubmit}
          onFreshSubmitSettled={settleFreshSubmit}
          onDismiss={dismissChat}
          onStagedActionClick={handleChatActionClick}
          sourceLabel="Output"
        />
      )}
    </View>
  )
}
